import { DateTime } from 'luxon';
/**
 * Creates a year-month string in the format 'YYYY-MM' from separate year and month values.
 *
 * @param year - The year (e.g., 2025)
 * @param month - The month (1-12, where 1 is January)
 * @returns A string in the format 'YYYY-MM' (e.g., '2025-03')
 */
export const toYearMonth = (year, month) => {
    // Ensure month is within valid range
    if (month < 1 || month > 12) {
        throw new Error(`Invalid month: ${month}. Month must be between 1 and 12.`);
    }
    // Format month to always have two digits
    const formattedMonth = month.toString().padStart(2, '0');
    return `${year}-${formattedMonth}`;
};
/**
 * Utility to split a year-month string in YYYY-MM format into separate year and month components.
 *
 * @param yearMonth - A string in the format "YYYY-MM" (e.g., "2025-05")
 * @returns An object with separate year and month properties as numbers
 *
 * @example
 * // Returns { year: 2025, month: 5 }
 * splitYearMonth("2025-05");
 */
export const fromYearMonth = (yearMonth) => {
    // Parse year and month from the YYYY-MM string
    const [yearStr, monthStr] = yearMonth.split('-');
    // Convert to numbers
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    // Validate the parsed values
    if (isNaN(year) || isNaN(month)) {
        throw new Error('Invalid yearMonth format. Expected "YYYY-MM".');
    }
    return { year, month };
};
/**
 * Converts a dollar amount to pennies (cents).
 *
 * @param amount - The dollar amount to convert.
 * @returns The equivalent amount in pennies (cents).
 */
export const toPennies = (amount) => Math.round(amount * 100);
/**
 * Converts an amount in pennies (cents) to dollars.
 *
 * @param pennies - The amount in pennies (cents) to convert.
 * @returns The equivalent amount in dollars.
 */
export const toDollars = (pennies) => pennies / 100;
/**
 * Calculates (part / total) * 100, then clamps the result to [0, 100]
 * and returns a whole number (rounded).
 *
 * @param part  - The numerator portion.
 * @param total - The denominator (if zero or negative, result is 0).
 * @returns The integer percentage from 0 to 100.
 */
export function toPercentage(part, total) {
    if (total <= 0) {
        return 0;
    }
    const ratio = part / total;
    const clamped = Math.max(0, Math.min(1, ratio));
    return Math.round(clamped * 100);
}
/**
 * Formats a timezone string by replacing underscores with spaces.
 * Used for displaying timezone names in a more readable format.
 * Example: "America/New_York" becomes "America/New York"
 *
 * @param timezone - The timezone string to format.
 * @returns The formatted timezone string with spaces instead of underscores.
 */
export const formatTimeZone = (timezone) => {
    return timezone.replace(/_/g, ' ');
};
/**
 * Converts a BudgetLocale enum to a locale string.
 * @param locale The BudgetLocale enum value
 * @returns The locale string (e.g. "en-US" for EN_US)
 */
export function toLocaleString(locale) {
    const [lang, country] = locale.split('_');
    return `${lang.toLowerCase()}-${country}`;
}
/**
 * Converts an ISO date string to a local date (YYYY-MM-DD) in the specified timezone.
 * If no date is provided, returns today's date in the specified timezone.
 *
 * @param timeZone - The timezone to convert the date to (e.g., 'America/New_York')
 * @param isoDate - Optional ISO date string to convert
 * @returns Local date string in YYYY-MM-DD format
 * @throws Error if date conversion fails
 */
export function toLocalDate(timeZone, isoDate) {
    let localDate;
    if (!isoDate) {
        // If no date exists, use today's date in the specified timezone
        localDate = DateTime.now().setZone(timeZone).toISODate();
    }
    else {
        // Parse the date and convert to the specified timezone
        const dateTime = DateTime.fromISO(isoDate);
        if (!dateTime.isValid) {
            throw new Error(`Invalid date format: ${isoDate}`);
        }
        // Set the timezone and extract just the date portion in ISO format (YYYY-MM-DD)
        localDate = dateTime.setZone(timeZone).toISODate();
    }
    if (!localDate) {
        throw new Error('Could not convert to local date');
    }
    return localDate;
}
/**
 * Converts an ISO date string to Unix epoch timestamp in seconds.
 *
 * @param isoDateString - ISO formatted date string (e.g., '2023-06-15')
 * @returns Unix timestamp in seconds
 * @example
 * // Returns 1686787200 for '2023-06-15'
 * const timestamp = isoToEpochSeconds('2023-06-15');
 */
export function isoToEpochSeconds(isoDateString) {
    return DateTime.fromISO(isoDateString).toUnixInteger();
}
